import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AuthImage from "@/components/AuthImage";
import { toast } from "sonner";
import { UploadSimple, Storefront, FloppyDisk, Trash } from "@phosphor-icons/react";

export default function Settings() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({ shop_name: "", address: "", phone: "", email: "", logo_path: null, labor_rate_usd_per_hour: 0, parts_markup_percent: 0, sales_tax_percent: 0 });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: shop } = await api.get("/shop");
        setProfile({
          shop_name: shop.shop_name || "",
          address: shop.address || "",
          phone: shop.phone || "",
          email: shop.email || "",
          logo_path: shop.logo_path || null,
          labor_rate_usd_per_hour: shop.labor_rate_usd_per_hour || 0,
          parts_markup_percent: shop.parts_markup_percent || 0,
          sales_tax_percent: shop.sales_tax_percent || 0,
        });
      } catch { toast.error("Failed to load settings"); }
      finally { setLoading(false); }
    })();
  }, []);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setProfile((p) => ({ ...p, logo_path: data.path }));
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/shop", profile);
      toast.success("Shop profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const clearLogo = () => setProfile((p) => ({ ...p, logo_path: null }));

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="text-xs font-mono-tech text-muted-foreground uppercase tracking-widest">// shop</div>
        <h1 className="font-display font-bold text-5xl tracking-tight">SHOP BRANDING</h1>
        <p className="text-muted-foreground mt-2">
          Set the info that shows on every printed customer report — logo, name, address, phone.
        </p>

        {loading ? (
          <div className="mt-8 border border-border rounded-md p-6 bg-card scan-line h-40" />
        ) : (
          <div className="mt-8 border border-border rounded-md p-6 bg-card space-y-6">
            {/* Logo */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Storefront weight="bold" className="w-5 h-5 text-primary" />
                <div className="font-display text-2xl tracking-tight">Logo</div>
              </div>
              <div className="flex items-start gap-5">
                <div className="w-32 h-32 border border-border rounded-md bg-background flex items-center justify-center overflow-hidden">
                  {profile.logo_path ? (
                    <AuthImage path={profile.logo_path} alt="Shop logo" className="max-w-full max-h-full object-contain" testid="shop-logo-preview" />
                  ) : (
                    <div className="text-xs text-muted-foreground font-mono-tech">NO LOGO</div>
                  )}
                </div>
                <div className="flex-1">
                  <label className="inline-flex items-center gap-2 border border-border rounded-md px-4 py-3 cursor-pointer hover:border-primary/50 transition-colors" data-testid="logo-upload-label">
                    <UploadSimple weight="bold" className="w-4 h-4" />
                    <span className="text-sm">{uploading ? "Uploading..." : "Upload logo (PNG, JPG, WebP · 8 MB max)"}</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
                      className="hidden"
                      data-testid="logo-upload-input"
                    />
                  </label>
                  {profile.logo_path && (
                    <button onClick={clearLogo} className="ml-3 text-sm text-muted-foreground hover:text-destructive inline-flex items-center gap-1" data-testid="clear-logo-btn">
                      <Trash weight="bold" className="w-4 h-4" /> Remove
                    </button>
                  )}
                  <div className="text-xs text-muted-foreground mt-2">Displayed at the top of every printed diagnostic report.</div>
                </div>
              </div>
            </div>

            <div className="border-t border-border" />

            {/* Details */}
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Shop name" value={profile.shop_name} onChange={(v) => setProfile({ ...profile, shop_name: v })} placeholder="Torres Auto Repair" testid="shop-name-input" />
              <Field label="Phone" value={profile.phone} onChange={(v) => setProfile({ ...profile, phone: v })} placeholder="(555) 123-4567" testid="shop-phone-input" />
              <Field label="Contact email" value={profile.email} onChange={(v) => setProfile({ ...profile, email: v })} placeholder="service@torresauto.com" testid="shop-email-input" />
              <div className="md:col-span-2">
                <Label className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider">Address</Label>
                <Textarea
                  value={profile.address}
                  onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                  placeholder="123 Grease Way, Denver, CO 80202"
                  className="mt-1 min-h-20 bg-input"
                  data-testid="shop-address-input"
                />
              </div>
            </div>

            <div className="border-t border-border" />

            {/* Pricing */}
            <div>
              <div className="font-display text-xl tracking-tight mb-1">Pricing</div>
              <div className="text-xs text-muted-foreground mb-3">
                Used to compute out-the-door totals on every customer report. Any field left at 0 is skipped.
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <NumberField
                  label="Labor rate ($ / hr)"
                  value={profile.labor_rate_usd_per_hour}
                  onChange={(v) => setProfile({ ...profile, labor_rate_usd_per_hour: v })}
                  placeholder="145"
                  step="1" min="0"
                  testid="labor-rate-input"
                  prefix="$"
                />
                <NumberField
                  label="Parts markup (%)"
                  value={profile.parts_markup_percent}
                  onChange={(v) => setProfile({ ...profile, parts_markup_percent: v })}
                  placeholder="35"
                  step="1" min="0"
                  testid="parts-markup-input"
                  suffix="%"
                />
                <NumberField
                  label="Sales tax (%)"
                  value={profile.sales_tax_percent}
                  onChange={(v) => setProfile({ ...profile, sales_tax_percent: v })}
                  placeholder="7.75"
                  step="0.01" min="0"
                  testid="sales-tax-input"
                  suffix="%"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => navigate("/dashboard")} className="min-h-12" data-testid="settings-cancel">
                Cancel
              </Button>
              <Button onClick={save} disabled={saving} className="min-h-12 bg-primary hover:bg-primary/90" data-testid="settings-save-btn">
                <FloppyDisk weight="bold" className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save profile"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider">{label}</Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 min-h-12 bg-input"
        data-testid={testid}
      />
    </div>
  );
}

function NumberField({ label, value, onChange, placeholder, step = "1", min = "0", testid, prefix, suffix }) {
  return (
    <div>
      <Label className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider">{label}</Label>
      <div className="mt-1 relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-mono-tech">{prefix}</span>}
        <Input
          type="number"
          value={value ?? 0}
          onChange={(e) => onChange(e.target.value === "" ? 0 : parseFloat(e.target.value))}
          placeholder={placeholder}
          step={step}
          min={min}
          className={`min-h-12 bg-input font-mono-tech ${prefix ? "pl-7" : ""} ${suffix ? "pr-7" : ""}`}
          data-testid={testid}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-mono-tech">{suffix}</span>}
      </div>
    </div>
  );
}
